import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  ExclamationTriangleIcon,
  ClockIcon,
  ArrowRightIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

interface EnforcementModalProps {
  isOpen: boolean;
  onClose: () => void;
  error: {
    error: string;
    message: string;
    current_count?: number;
    limit?: number;
    resource?: string;
    plan?: string;
    plan_status?: string;
  };
}

const EnforcementModal: React.FC<EnforcementModalProps> = ({
  isOpen,
  onClose,
  error
}) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleUpgrade = () => {
    navigate('/settings/billing');
    onClose();
  };

  const getModalContent = () => {
    switch (error.error) {
      case 'plan_limit_exceeded':
        return {
          title: 'Plan Limit Reached',
          icon: <ExclamationTriangleIcon className="w-12 h-12 text-yellow-500" />,
          description: error.message,
          details: error.current_count && error.limit ? (
            <div className="bg-gray-50 rounded-lg p-4 mt-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Current usage:</span>
                <Badge variant="secondary">
                  {error.current_count} / {error.limit} {error.resource}
                </Badge>
              </div>
            </div>
          ) : null,
          primaryAction: 'Upgrade Plan',
          secondaryAction: 'Cancel'
        };

      case 'trial_expired':
        return {
          title: 'Trial Expired',
          icon: <ClockIcon className="w-12 h-12 text-red-500" />,
          description: error.message,
          details: (
            <div className="bg-red-50 rounded-lg p-4 mt-4">
              <p className="text-sm text-red-700">
                Your free trial has ended. Upgrade to continue using Edyt's powerful customer success features.
              </p>
            </div>
          ),
          primaryAction: 'Upgrade Now',
          secondaryAction: 'Cancel'
        };

      case 'subscription_inactive':
        return {
          title: 'Subscription Issue',
          icon: <ExclamationTriangleIcon className="w-12 h-12 text-red-500" />,
          description: error.message,
          details: (
            <div className="bg-yellow-50 rounded-lg p-4 mt-4">
              <p className="text-sm text-yellow-700">
                Please update your payment method or contact support to resolve this issue.
              </p>
            </div>
          ),
          primaryAction: 'Manage Billing',
          secondaryAction: 'Cancel'
        };

      default:
        return {
          title: 'Action Required',
          icon: <ExclamationTriangleIcon className="w-12 h-12 text-gray-500" />,
          description: error.message || 'An issue occurred that requires your attention.',
          details: null,
          primaryAction: 'View Billing',
          secondaryAction: 'Cancel'
        };
    }
  };

  const content = getModalContent();

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md shadow-2xl border-0 bg-white/95 backdrop-blur-md">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-3">
              {content.icon}
              <span>{content.title}</span>
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all duration-200"
            >
              <XMarkIcon className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>
        
        <CardContent>
          <div className="space-y-4">
            <p className="text-gray-600">{content.description}</p>
            
            {content.details}
            
            <div className="flex space-x-3 pt-4">
              <Button
                onClick={onClose}
                variant="outline"
                className="flex-1"
              >
                {content.secondaryAction}
              </Button>
              <Button
                onClick={handleUpgrade}
                className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
              >
                {content.primaryAction}
                <ArrowRightIcon className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EnforcementModal;
